export interface ClaimActivityEntry {
  date: string;
  message: string;
}

export interface FinancialTransaction {
  transactionId: string;
  transactionDate: string;
  checkNumber: string;
  issueDate: string;
  checkPayees: string;
  paidSettlement?: number;
  paidExpense?: number;
  paidRecovery?: number;
}

export interface FinancialReserves {
  transactionId: string;
  transactionDate: string;
  caseReserveSettlement: number;
  caseReserveExpense: number;
}

export interface FinancialSummary {
  currentExperience: string;
  settlements: FinancialTransaction[];
  expenses: FinancialTransaction[];
  recoveries: FinancialTransaction[];
  reserves?: FinancialReserves | null;
}

export interface ClaimSummary {
  policyNumber: string;
  reportedDate?: string;
  timeOfIncident?: string;
  incidentDescription?: string;
  incidentType?: string;
  claimOpenedDate?: string;
  claimNumber: string;
  primaryAddressFirstLine?: string;
  claimStatusCode: string;
  claimStatusDescription?: string;
  lossDate: string;
  openDate?: string;
  claimDescription?: string;
  lossCauseDescription?: string;
  serviceProviderInfo?: ProviderInfo[];
  paymentInfo?: PaymentTransaction[];
  adjusterInfo?: AdjusterInfo;
  claimProgressStates?: StageState[];
  currentClaimProgressState?: ClaimProgressEnum;
  lossLocationAddress?: LossLocationAddress;
  autoClaimDetails?: AutoClaimDetails[];
  cvpRefNumber?: string;
  claimantName?: string;
  claimType?: string;
  displayName?: string;
  financialSummary?: FinancialSummary;
  totalPaid?: number;
  totalLossAmountPaid?: number;
  closeDate?: string;
  claimClosedDate?: string;
  insured?: { firstName: string; lastName: string };
  lineOfBusiness?: GenericProductType;
  businessName?: string;
}

export enum ThemeIdentifiers {
  AMFAM_THEME_ID = 'C46502ED595BAD41',
  MSA_THEME_ID = 'D8958C3071714434',
  LEMONADE_THEME_ID = 'C7A3B5E8F9D612AC',
}
export interface ThirdPartyInfoInterface {
  id: number;
  firstName?: string;
  lastName?: string;
  personInjured: boolean;
  phoneNumber?: string;
  phoneNumberValid: boolean;
  emailAddress?: string;
  emailAddressValid: boolean;
  vehicleInvolved: boolean;
  vehicleYear?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface DamageTypeOption {
  id: string;
  text: string;
  isSelected: boolean;
}

export interface AutoClaimDetails {
  year: string;
  make: string;
  model: string;
  vin: string;
}

export interface ClaimState {
  openClaims: ClaimSummary[] | undefined;
  closedClaims: ClaimSummary[] | undefined;
  draftClaims: ClaimSummary[] | undefined;
  hasHiddenClaims: boolean;
  // True when the claims-by-policy call came back partially or fully degraded.
  hasClaimsRetrievalIssue: boolean;
}

export interface LossLocationAddress {
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export enum ClaimProgressEnum {
  SUBMISSION = 'SUBMISSION',
  EVALUATION = 'EVALUATION',
  RESOLUTION = 'RESOLUTION',
  CLOSED = 'CLOSED',
}

export interface DamagePickerData {
  selectedDamageTypeOption: string;
  selectedDamageCodes: string[];
  damageDescription: string;
}

export enum DamageTypes {
  PARTIAL_DAMAGE = 'partialDamage',
  TOTAL_LOSS = 'totalLoss',
  NO_DAMAGE = 'noDamage',
}
export interface AdjusterInfo {
  adjusterName: string;
  adjusterEmail: string;
  adjusterPhoneNumber?: string;
}
export interface PaymentTransaction {
  PayeeName: string;
  Amount: string;
  TransactionDate: string;
  Payees: PayeeCollection[];
}

export interface PayeeCollection {
  claimRoles?: ClaimRole[];
}

export interface ClaimRole {
  roleCode?: string;
}

export enum ServiceType {
  EMERGENCY_SERVICE_TYPE = 'emergencyserv',
  WATER_MITIGATION_TYPE = 'watermitigation',
}

export enum ServiceTypeCode {
  EMERGENCY_SERVICE_TYPE_CODE = 'propemerg',
  WATER_MITIGATION_TYPE_CODE = 'propwatermitigation',
  DOCUMENT_DAMAGE_TYPE_CODE = 'propplanr',
  REPAIR_TYPE_CODE = 'autobodyrepair',
  RENTAL_TYPE_CODE = 'autorental',
  TOWING_TYPE_CODE = 'towing',
}
export interface ProviderInfoFromClaimDetails {
  serviceRequestID: string;
  serviceType: string;
  specialist: Specialist;
  status?: string;
}

export interface ClaimDetailResponse {
  response: any[];
}

export interface AddressInfo {
  firstName: string;
  lastName: string;
  addresses: Address[];
  contactType: string;
}

export interface Address {
  primaryAddress: boolean;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface Specialist {
  displayName: string;
}
export interface IconStates {
  state: IconStateType;
  headerContent?: string;
  subHeaderContent?: string;
}

export enum IconStateType {
  FAIL_STATE = 'FAIL_STATE ',
  WARNING_STATE = 'WARNING_STATE',
  SUCCESS_STATE = 'SUCCESS_STATE',
  NONE = 'NONE',
}
export interface ContactAdjusterInternalEmail {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  comment: string;
  claimNumber: string;
  adjusterEmail: string;
  adjusterName: string;
}

export interface PropertyEligibilityServiceCallArguments {
  urlKey: string;
  sessionId: string;
  authKey: string;
  claimNumber: string;
  zipCode: string;
}

export interface AutoEligibilityServiceCallArguments {
  urlKey: string;
  sessionId: string;
  authKey: string;
  claimNumber: string;
  vin: string;
}

export interface CreatePlnarProjectPayload {
  reporter: PlnarReporter;
  address?: PlnarAddress;
  claimNumber: string;
  sourceSystem: string;
  lossCause?: string;
}

interface PlnarReporter {
  email: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
}

export interface PlnarAddress {
  address1: string;
  address2: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
}

export interface ClaimPlnarInfo {
  address: PlnarAddress;
  lossCause: string;
}

export interface CreateServicePayload {
  claimNumber: string;
  zipCode: string;
  serviceType?: string;
  serviceTypeCode?: string;
  vendorName?: string;
  subServices?: SubServices[] | undefined;
  plnar?: boolean;
}
export interface SubServices {
  code: string;
}
export interface CreateServiceResponse {
  response: CreateServiceResponseInResponse;
}
interface CreateServiceResponseInResponse {
  code: number;
  updatedAuthToken: string;
}
export interface PropertyEligibilityServicesResponse {
  response: PropertyEligibilityServicesInfo;
}

export interface AutoEligibilityServicesResponse {
  response: AutoEligibilityServicesInfo;
}
export interface PropertyEligibilityServicesInfo {
  eligibleServices: PropertyServicesInfo[];
  plnarEligible: boolean;
  status: Status;
  opCo: string;
  updatedAuthToken: string;
}

export interface AutoEligibilityServicesInfo {
  eligibleServices: AutoServiceInfo[];
  status: Status;
  claimNumber: string;
  vin: string;
  numberOfCustomerPhotos: number;
}

interface Status {
  reason: string;
  maxMessageLevel: string;
  zipCode: string;
  code: number;
  transactionID: string;
  claimNumber: string;
  responseTime: number;
}

export interface PropertyServicesInfo {
  providers: ProviderInfo[];
  serviceOpen: false;
  serviceCustomerCallinMsgCode: string;
  serviceType: string;
  serviceTypeCode: string;
  serviceName: string;
  serviceDescription: string;
}

export interface AutoServiceInfo {
  coverageCode: string;
  coverageDescription: string;
  serviceCode: string;
  serviceCustomerCallInMsgCode: string;
  serviceDependenciesSatisfied: boolean;
  serviceDependentOnSRCode: string;
  serviceDescription: string;
  serviceOpen: boolean;
  serviceRequestDisclaimer: ServiceRequestDisclaimer[];
}

export interface PhotoInfo {
  id: string;
  url: string;
  category: string;
  description: string;
  mimeType: string;
  dateCreated: string;
}

export interface OptimizelyFlags {
  autoRentalShopSearchSelection: boolean;
  autoPhotoUploadCCC: boolean;
  autoDrpSearchSelection: boolean;
}

export interface ServiceRequestDisclaimer {
  serviceDisclaimerCode: string;
  serviceDisclaimerDescription: string;
}
export interface ProviderInfo {
  serviceTypeName: string;
  serviceAvailable: string;
  providerName: string;
  providerID?: string;
  providerPhone: string;
  providerUrl?: string | undefined;
  providerAddress?: string;
  numberUploaded?: string | undefined;
  subServiceTypes?: SubServiceType[] | undefined;
  serviceCreationStatus?: boolean | undefined;
  serviceRequestDate?: string;
  failedPhotoItems?: FailedPhotoItem[];
}

export interface FailedPhotoItem {
  label: string;
  fileName: string;
}

export interface SubServiceType {
  subServiceTypeCode: string;
  subServiceType: string;
  subServiceAvailable: string;
}

export interface StageState {
  name: string;
  completed: boolean;
  viewActive: boolean;
  skipped: boolean;
  showProgress: boolean;
  orderOfDisplay: number;
  id: string;
  isEligible?: boolean;
  isServiceOpen?: boolean;
}

export enum OpCo {
  MSA = 'msa',
  MSA_THIRD_PARTY = 'msaThirdParty',
  AMFAM = 'amfam',
  AMFAM_THIRD_PARTY = 'amfamThirdParty',
  HOMESITE = 'homesite',
  // Lemonade is its own partner (customer group 660), separate from Homesite. It keeps
  // Homesite's Converse question set — see the partner-id mapping in api/converseApi.ts.
  LEMONADE = 'lemonade',
  GEICO = 'geico',
  PROGRESSIVE = 'progressive',
}

export enum PartnerName {
  connectNonPartner = 'connect non partner',
  amfamExclusive = 'amfam exclusive',
  osi = 'onstar insurance (osi)',
  costco = 'costco',
  ameriprise = 'ameriprise',
}

export enum PartnerId {
  AFICS = 'AFICS',
}

export enum SourceSystem {
  MSA = 'MSAWebApplication',
  AMFAM = 'MyAccountWebApplication',
  CAH = 'ConnectWebApplication',
}

export enum Features {
  REPORT_STAGE = 'REPORT_STAGE',
  SUBMIT_STAGE = 'SUBMIT_STAGE',
  EMERGENCY_SERVICES = 'EMERGENCY_SERVICES',
  WATER_REMOVAL = 'WATER_REMOVAL',
  DOCUMENT_DAMAGE = 'DOCUMENT_DAMAGE',
  REPAIR = 'REPAIR',
  PHOTOS = 'PHOTOS',
  RENTAL = 'RENTAL',
  COMPLETE_STAGE = 'COMPLETE_STAGE',
}

export class EligibleServicesConstants {
  public static readonly DRP = 'drp';
  public static readonly RENTAL = 'RENTAL';
  public static readonly CVP = 'CVP';
}

export class PartnerFeatures {
  public static REPORT_STAGE: Array<string> = [OpCo.MSA, OpCo.AMFAM, OpCo.HOMESITE];
  public static EMERGENCY_SERVICES: Array<string> = [OpCo.MSA, OpCo.AMFAM, OpCo.HOMESITE];
  public static WATER_REMOVAL: Array<string> = [OpCo.MSA, OpCo.AMFAM, OpCo.HOMESITE];
  public static DOCUMENT_DAMAGE: Array<string> = [OpCo.MSA, OpCo.AMFAM, OpCo.HOMESITE];
  public static COMPLETE_STAGE: Array<string> = [OpCo.MSA, OpCo.AMFAM, OpCo.HOMESITE];
}

export enum StageNames {
  REPORT = 'Report',
  EMERGENCY_SERVICES = 'Emergency Services',
  WATER_REMOVAL = 'Water Removal',
  DOCUMENT_DAMAGE = 'Document Damage',
  REPAIR = 'Repair',
  PHOTOS = 'Photos',
  RENTAL = 'Rental',
  TOWING = 'Towing',
  COMPLETE = 'Complete',
  EVALUATE = 'Evaluate',
  RESOLVE = 'Resolve',
  CLOSE = 'Close',
  SUBMIT = 'Submit',
}

export enum SearchContext {
  REPAIR = 'Repair',
  RENTAL = 'Rental',
}

export enum ThemeType {
  MSA,
  AMFAM,
  LEMONADE,
  GEICO,
  PROGRESSIVE,
  HOMESITE,
}

export enum GenericProductType {
  Auto,
  Home,
  Life,
  Mixed,
  Other,
  Umbrella,
  CommercialAuto,
  FarmRanch,
  GeneralLiability,
  Renters,
  CommercialProperty,
  WorkersComp,
}

export enum PolicyStatus {
  Cancelled,
  Inforce,
  Inactive,
  Scheduled,
  Other,
}

export interface PaginationData {
  pageCount: number;
  starting: number;
  ending: number;
  totalResultCount: number;
}

export class FullAddress {
  houseNumber: string | null | undefined;
  streetName: string | null | undefined;
  cityName: string | null | undefined;
  stateName: string | null | undefined;
  countryName: string | null | undefined;
  zipCode: string | null | undefined;
  latitude: number | null | undefined;
  longitude: number | null | undefined;
}

export interface PlaceType {
  description: string;
  structured_formatting: StructuredFormatting;
  place_id: string;
}

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings: readonly MainTextMatchedSubstrings[];
}

export interface BackNavigationClick {
  backNavigationClickHandler: () => void;
}

export interface SearchButtonClick {
  searchButtonClickHandler: () => void;
}

export interface AddressUpdate {
  addressUpdateHandler: (fullAddress: FullAddress) => void;
}

export interface DistanceUpdate {
  distanceUpdateHandler: (distance: string) => void;
}
// Module augmentation to support custom object within Theme object

declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      colors: {
        textSecondary: string;
        primaryColor: string;
        secondaryColor: string;
        headerTopBorderColor: string;
        headerBottomBorderColor: string;
        headerBackgroundColor: string;
        listSelectionBackgroundColor: string;
        mainBackgroundColor: string;
        footerTopBorderColor: string;
        visitWebLinkColor: string;
        primaryButtonHoverColor: string;
        disabledButtonBackgroundColor: string;
        disabledButtonTextColor: string;
        accordionChevronColor: string;
        radioButtonColor: string;
        radioButtonSelectedColor: string;
        bodyColor: string;
        defaultBorderColor: string;
      };
      links: {
        termsAndConditionsLink: string;
      };
      text: {
        contactNumber: string;
      };
      styles: {
        defaultRadioButtonSize: string;
        defaultCheckBoxSize: string;
        defaultBorderWidth: string;
        defaultInputHeight: string;
        defaultBorderRadius: string;
        linearProgressBarStyle: {
          borderRadius: string;
          primaryBarColor: string;
          secondaryBarColor: string;
          header: {
            fontSize: number;
            fontWeight: number;
            color: string;
          };
          description: {
            fontSize: number;
            fontWeight: number;
            color: string;
          };
        };
        radioandcheckboxStyle: {
          defaultBackground: string;
          defaultBorder: string;
          hoverBackground: string;
          hoverBorder: string;
          selectedBackground: string;
          selectedBorder: string;
        };
        modalLoadingIndicatorStyle: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          trackColor: string;
          spinnerColor: string;
          loadingTextColor: string;
          backDropColor: string;
        };
        titleType2: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        titleType3: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        titleType4: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
          lineHeight: string;
          fontStyle: string;
        };
        body2: {
          fontFamily: string;
          fontSize: number;
          fontWeight: number;
          letterSpacing: number;
          lineHeight: string;
          textAlign: string;
          color: string;
        };
        body2Bold: {
          fontFamily: string;
          fontSize: number;
          fontWeight: number;
          letterSpacing: number;
          lineHeight: string;
          textAlign: string;
          color: string;
        };
        damagePicker: {
          hoverFill: string;
          outline: string;
          selectedFill: string;
          stroke: string;
          text: string;
        };
        modalHeader: {
          fontSize: number;
          fontWeight: number;
          letterSpacing: number;
          lineHeight: string;
          textAlign: string;
          color: string;
          backgroundColor: string;
          closeIconColor: string;
        };
        adjusterCardPhoneNumberAndEmailStyle: {
          fontWeight: number;
          fontSize: number;
          textDecoration: string;
          color: string;
          fontFamily: string;
          marginTop: string;
          marginLeft: string;
        };
        adjusterCardPhoneNumberAndEmailCardStyle: {
          border: string;
          borderRadius: string;
          borderColor: string;
        };
        confirmationSuccessIcon: {
          color: string;
          height: string;
          width: string;
          fontSize: number;
        };
        confirmationErrorIcon: {
          color: string;
          height: string;
          width: string;
        };
        confirmationWarningIcon: {
          color: string;
          height: string;
          width: string;
        };
        pendingTasksBoxStyle: {
          backgroundColor: string;
          padding: string;
          fontSize: number;
          borderRadius: string;
          border: string;
          borderColor: string;
        };
        errorWarningIconColor: string;
        secondaryDestructiveButton: {
          backGroundColor: string;
          borderColor: string;
          color: string;
          hoverBackgroundColor: string;
          hoverTextColor: string;
        };
        primaryDestructiveButton: {
          backGroundColor: string;
          borderColor: string;
          color: string;
          hoverBackgroundColor: string;
          hoverTextColor: string;
        };
        genericPrimaryButton: {
          backGroundColor: string;
          borderColor: string;
          color: string;
          hoverBackgroundColor: string;
          hoverTextColor: string;
          border: string;
          textTransform: string;
          height: string;
          minWidth: string;
        };
        genericSecondaryButton: {
          backGroundColor: string;
          borderColor: string;
          color: string;
          hoverBackgroundColor: string;
          hoverTextColor: string;
          border: string;
          textTransform: string;
          height: string;
        };
        genericTertiaryButton: {
          backGroundColor: string;
          borderColor: string;
          color: string;
          hoverBackgroundColor: string;
          hoverTextColor: string;
          border: string;
          textTransform: string;
          height: string;
        };
        genericDestructiveTertiaryButton: {
          backGroundColor: string;
          borderColor: string;
          color: string;
          hoverBackgroundColor: string;
          hoverTextColor: string;
          border: string;
          textTransform: string;
          height: string;
        };
        confirmationSubHeader: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
          textDecorationColor: string;
        };
        confirmationList: {
          paddingLeft: string;
          paddingTop: string;
          listStyle: string;
        };
        confirmationListElement: {
          display: string;
          paddingTop: string;
          paddingBottom: string;
          paddingLeft: string;
        };
        serviceConfirmationHeader: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
          marginTop?: string;
        };
        ctaNextButton: {
          order: number;
          color: string;
          backgroundColor: string;
          marginLeft?: string;
          marginRight?: string;
          boxShadow?: number;
          textTransform?: string;
          minHeight?: string;
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
          width?: string;
        };
        ctaCancelButton: {
          order: number;
          color: string;
          backgroundColor: string;
          marginLeft?: string;
          marginRight?: string;
          textTransform?: string;
          textDecoration?: string;
          fontSize?: number;
          fontWeight?: number;
        };
        ctaBackButton: {
          order: number;
          color: string;
          backgroundColor: string;
          marginLeft?: string;
          marginRight?: string;
          borderColor?: string;
          textTransform?: string;
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
          border?: string;
          minHeight?: string;
        };
        ctaBackButtonIsLinkOnDesktop: boolean;
        ctaBackButtonIsLinkOnMobile: boolean;
        showReviewItemDividerLineOnMobile: boolean;
        showButtonWhenServicesSkipped: boolean;
        showRadioButtonAtTop: boolean;
        chevronFirst: boolean;
        chevronHeight: string;
        chevronWidth: string;
        singleServiceSummaryHr: boolean;
        ctaSkipButton: {
          order: number;
          color: string;
          backgroundColor: string;
          marginLeft?: string;
          marginRight?: string;
          borderColor?: string;
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
          minHeight?: string;
        };
        ctaErrorButton: {
          color: string;
          backgroundColor: string;
          boxShadow?: number;
          textTransform?: string;
          borderColor?: string;
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
        };
        ctaPrimaryErrorButton: {
          color: string;
          backgroundColor: string;
          boxShadow?: number;
          textTransform?: string;
          borderColor?: string;
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
        };
        errorButtonHoverColor: string;
        ctaFlexSpace: {
          order: number;
          flexGrow: number;
        };
        expandCollapseButton: {
          color: string;
          backgroundColor: string;
          borderColor?: string;
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          fontStyle?: string;
          textTransform?: string;
          textDecoration?: string;
        };
        expandCollapseDisabledButton: {
          color: string;
          backgroundColor: string;
          borderColor?: string;
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          fontStyle?: string;
          textTransform?: string;
        };

        accordionHeaderButtonStyle: {
          backgroundColor: string;
          cursor: string;
          display: string;
          alignItems: string;
          border: string;
          outline: string;
          textTransform: string;
          paddingLeft: string;
          marginTop: string;
          marginLeft: string;
          marginBottom: string;
        };
        titleType1: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        accordionHeader: {
          fontFamily: string;
          fontWeight: number;
          letterSpacing: number;
          lineHeight: string;
          textAlign: string;
          fontSize: number;
          color: string;
        };
        accordionRowHeader: {
          fontFamily: string;
          fontWeight: number;
          letterSpacing: number;
          lineHeight: string;
          textAlign: string;
          fontSize: number;
          color: string;
          variant: string;
          elevation: number;
        };
        accordionRowGridItemStyle: {
          margin: string;
        };
        claimSummaryDetailItemStyle: {
          margin: string;
        };
        adjusterNameStyle: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        adjusterShortNameStyle: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        primaryDescription: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
          lineHeight: string;
        };
        primaryLabel: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        secondaryDescription: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        formLabelStyle: {
          fontFamily: string;
          fontSize: number;
          fontWeight: number;
          letterSpacing: number;
          lineHeight: string;
          textAlign?: 'left';
          color: string;
        };
        formTextStyle: {
          fontFamily: string;
          fontSize: number;
          fontWeight: number;
          letterSpacing: number;
          lineHeight: string;
          textAlign: string;
          color: string;
        };
        disclaimerFont: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        claimSummaryDateFont: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        monthShortNameFont: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        errorSubcaseFont: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        providerTitleStyle: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        phoneNumberAndWebSiteStyle: {
          fontWeight: string;
          fontSize: number;
          textDecoration: string;
          color: string;
          fontFamily: string;
        };
        serviceProviderCardStyle: {
          padding: string;
          '&:hover': {
            border: string;
          };
          border: string;
          boxShadow: string;
          borderRadius: string;
        };
        serviceProviderSelectedCardStyle: {
          padding: string;
          border: string;
          color: string;
          boxShadow: string;
          borderRadius: string;
        };
        contactCardHeaderRowStyle: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
          marginBottom?: string;
          marginTop?: string;
        };
        rentalAmountStyle: {
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
          letterSpacing?: number;
          lineHeight?: string;
          textAlign?: string;
          color?: string;
        };
        editOnReviewStyle: {
          width?: string;
          textAlign?: string;
          marginRight?: string;
          textDecoration?: string;
          cursor?: string;
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
        };
        themeType?: {
          currentThemeType: ThemeType;
        };
        contactCardRowStyling: {
          backgroundColor: string;
          padding: string;
        };
        oddRow: {
          display: string;
          alignItems: string;
          backgroundColor: string;
        };
        evenRow: {
          display: string;
          alignItems: string;
          backgroundColor: string;
        };
        hrWrapperOdd: {
          background: string;
          flexBasis: string;
        };
        hrWrapperEven: {
          background: string;
          flexBasis: string;
        };
        hrStyle: string;
        ctaHrStyle?: string;
        progressStepperComplete?: string;
        progressStepperSkipped?: string;
        progressStepperBackground?: string;
        hrDashedStyle?: string;
        logoWidth?: string;
        logoHeight?: string;
        serviceProviderCardHr: string;
        serviceProviderSummaryHr?: string;
        singleServiceSummaryHrStyle?: string;
        modalHeaderHrStyle: {
          height: string;
          backgroundColor: string;
          border: string;
          width: string;
        };
        listItemDividerHrStyle: {
          height: string;
          backgroundColor: string;
          border: string;
          width: string;
        };
        genericModalAlertBoxStyle: {
          distributionDirection: string;
          contentJustifyType: string;
          alignmentType: string;
          spacing: number;
          primaryActionButtonStyle: {
            backgroundColor: string;
            borderColor: string;
            color: string;
            width: string;
            hoverBackgroundColor: string;
            hoverTextColor: string;
            fontSize: number;
            fontWeight: number;
            fontFamily: string;
            textTransform: string;
            buttonOrder?: number;
          };
        };
        genericModalDestructiveAlertBoxStyle: {
          distributionDirection: string;
          contentJustifyType: string;
          alignmentType: string;
          spacing: number;
          primaryActionButtonStyle: {
            backgroundColor: string;
            borderColor: string;
            color: string;
            width: string;
            hoverBackgroundColor: string;
            hoverTextColor: string;
            fontSize: number;
            fontWeight: number;
            fontFamily: string;
            textTransform: string;
            buttonOrder: number;
          };
        };
        genericModalCancelAlertBoxStyle: {
          distributionDirection: string;
          contentJustifyType: string;
          alignmentType: string;
          spacing: number;
          primaryActionButtonStyle: {
            backgroundColor: string;
            borderColor: string;
            color: string;
            width: string;
            hoverBackgroundColor: string;
            hoverTextColor: string;
            fontSize: number;
            fontWeight: number;
            fontFamily: string;
            textTransform: string;
            buttonOrder: number;
          };
        };
        singleServiceSummaryDashedHr?: boolean;
        showGapBetweenCtaButtons?: boolean;
        showSmallRadioButton?: boolean;
        showBorderAroundListItems?: boolean;
        showUnderlinedClaimActivityHeader?: boolean;
        showUnderlinedAccordionRowHeader?: boolean;
        listItemBorderSelectedStyle?: string;
        listItemBorderDefaultStyle?: string;
        underLineModalHeader?: boolean;
        underLineModalDescription?: boolean;
        showIconOnRisks?: boolean;
        showThickBorderForUnselectedListItem?: boolean;
        modalActionButtonDirection: string;
        showModalPopUpOnFNOLCancel: boolean;
        activityIndicatorHasElevation: boolean;
        endIcon: {
          marginLeft: string;
          marginRight: string;
        };
        progressBar: {
          backgroundColor: string;
          fillColor: string;
          progressTextColor: string;
          statusBarsProgressText: string;
          borderColor: string;
        };
        checkBox: {
          defaultBackground: string;
          defaultBorder: string;
          selectedBackground: string;
          selectedBorder: string;
          selectedCheckmarkColor: string;
        };
        goToClaimButtonGridStyle: {
          marginLeft: string;
          paddingTop: string;
          justifyContent: string;
          display: string;
        };
        borderRadius: {
          radiusDefault: string;
          radiusInput: string;
          radiusModal: string;
          radiusProgressBar: string;
        };
      };
      icons?: {
        iconAuto?: string;
        iconProperty?: string;
        iconVinLocations?: string;
      };
    };
  }
  interface ThemeOptions {
    custom?: {
      colors?: {
        primaryColor?: string;
        secondaryColor?: string;
        headerTopBorderColor?: string;
        headerBottomBorderColor?: string;
        headerBackgroundColor?: string;
        listSelectionBackgroundColor?: string;
        mainBackgroundColor?: string;
        footerTopBorderColor?: string;
        visitWebLinkColor?: string;
        primaryButtonHoverColor?: string;
        disabledButtonBackgroundColor?: string;
        disabledButtonTextColor?: string;
        accordionChevronColor?: string;
        radioButtonColor?: string;
        radioButtonSelectedColor?: string;
        bodyColor?: string;
        defaultBorderColor?: string;
      };
      links?: {
        termsAndConditionsLink?: string;
      };
      text?: {
        contactNumber?: string;
      };
      styles?: {
        defaultRadioButtonSize?: string;
        defaultCheckBoxSize?: string;
        defaultBorderWidth?: string;
        defaultInputHeight?: string;
        linearProgressBarStyle?: {
          borderRadius?: string;
          primaryBarColor?: string;
          secondaryBarColor?: string;
          header?: {
            fontSize?: number;
            fontWeight?: number;
            color?: string;
          };
          description?: {
            fontSize?: number;
            fontWeight?: number;
            color?: string;
          };
        };
        defaultBorderRadius?: string;
        radioandcheckboxStyle?: {
          defaultBackground?: string;
          defaultBorder?: string;
          hoverBackground?: string;
          hoverBorder?: string;
          selectedBackground?: string;
          selectedBorder?: string;
        };
        modalLoadingIndicatorStyle?: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          trackColor?: string;
          spinnerColor?: string;
          loadingTextColor?: string;
          backDropColor?: string;
        };
        titleType2?: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
          textDecorationColor?: string;
        };
        titleType3?: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
        };
        titleType4?: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
          lineHeight?: string;
          fontStyle?: string;
        };
        body2?: {
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
          letterSpacing?: number;
          lineHeight?: string;
          textAlign?: string;
          color?: string;
        };
        body2Bold?: {
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
          letterSpacing?: number;
          lineHeight?: string;
          textAlign?: string;
          color?: string;
        };
        damagePicker?: {
          hoverFill?: string;
          outline?: string;
          selectedFill?: string;
          stroke?: string;
          text?: string;
        };
        modalHeader?: {
          fontSize?: number;
          fontWeight?: number;
          letterSpacing?: number;
          lineHeight?: string;
          textAlign?: string;
          color?: string;
          backgroundColor?: string;
          closeIconColor?: string;
        };
        adjusterCardPhoneNumberAndEmailStyle?: {
          fontWeight?: number;
          fontSize?: number;
          textDecoration?: string;
          color?: string;
          fontFamily?: string;
          marginTop?: string;
          marginLeft?: string;
        };
        adjusterCardPhoneNumberAndEmailCardStyle?: {
          border?: string;
          borderRadius?: string;
          borderColor?: string;
        };
        confirmationSuccessIcon?: {
          color: string;
          height: string;
          width: string;
          fontSize?: number;
        };
        confirmationErrorIcon?: {
          color: string;
          height: string;
          width: string;
        };
        confirmationWarningIcon?: {
          color: string;
          height: string;
          width: string;
        };
        pendingTasksBoxStyle?: {
          backgroundColor?: string;
          padding?: string;
          fontSize?: number;
          borderRadius?: string;
          border?: string;
          borderColor?: string;
        };
        errorWarningIconColor?: string;
        secondaryDestructiveButton?: {
          backGroundColor?: string;
          borderColor?: string;
          color?: string;
          hoverBackgroundColor?: string;
          hoverTextColor?: string;
        };
        primaryDestructiveButton?: {
          backGroundColor?: string;
          borderColor?: string;
          color?: string;
          hoverBackgroundColor?: string;
          hoverTextColor?: string;
        };
        genericPrimaryButton?: {
          backGroundColor?: string;
          borderColor?: string;
          color?: string;
          hoverBackgroundColor?: string;
          hoverTextColor?: string;
          border?: string;
          textTransform?: string;
          height?: string;
          minWidth?: string;
        };
        genericSecondaryButton?: {
          backGroundColor?: string;
          borderColor?: string;
          color?: string;
          hoverBackgroundColor?: string;
          hoverTextColor?: string;
          border?: string;
          textTransform?: string;
          height?: string;
        };
        genericTertiaryButton?: {
          backGroundColor?: string;
          borderColor?: string;
          color?: string;
          hoverBackgroundColor?: string;
          hoverTextColor?: string;
          border?: string;
          textTransform?: string;
          height?: string;
        };
        genericDestructiveTertiaryButton?: {
          backGroundColor?: string;
          borderColor?: string;
          color?: string;
          hoverBackgroundColor?: string;
          hoverTextColor?: string;
          border?: string;
          textTransform?: string;
          height?: string;
        };
        confirmationSubHeader?: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
          textDecorationColor?: string;
        };
        confirmationList?: {
          paddingLeft: string;
          listStyle: string;
          paddingTop: string;
        };
        confirmationListElement?: {
          display: string;
          paddingTop: string;
          paddingBottom: string;
          paddingLeft?: string;
        };
        ctaNextButton?: {
          order: number;
          color: string;
          backgroundColor: string;
          marginLeft?: string;
          marginRight?: string;
          boxShadow?: number;
          textTransform?: string;
          minHeight?: string;
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
          width?: string;
        };
        ctaCancelButton?: {
          order: number;
          color: string;
          backgroundColor: string;
          marginLeft?: string;
          marginRight?: string;
          textTransform?: string;
          textDecoration?: string;
          fontSize?: number;
          fontWeight?: number;
        };
        ctaBackButton?: {
          order: number;
          color: string;
          backgroundColor: string;
          marginLeft?: string;
          marginRight?: string;
          borderColor?: string;
          textTransform?: string;
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
          border?: string;
          minHeight?: string;
        };
        ctaBackButtonIsLinkOnDesktop?: boolean;
        ctaBackButtonIsLinkOnMobile?: boolean;
        showReviewItemDividerLineOnMobile?: boolean;
        showButtonWhenServicesSkipped?: boolean;
        showRadioButtonAtTop?: boolean;
        chevronFirst?: boolean;
        chevronHeight?: string;
        chevronWidth?: string;
        singleServiceSummaryHr?: boolean;
        ctaSkipButton?: {
          order: number;
          color: string;
          backgroundColor: string;
          marginLeft?: string;
          marginRight?: string;
          borderColor?: string;
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
          minHeight?: string;
        };
        ctaErrorButton?: {
          color: string;
          backgroundColor: string;
          boxShadow?: number;
          textTransform?: string;
          borderColor?: string;
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
        };
        ctaPrimaryErrorButton?: {
          color: string;
          backgroundColor: string;
          boxShadow?: number;
          textTransform?: string;
          borderColor?: string;
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
        };
        errorButtonHoverColor?: string;
        ctaFlexSpace?: {
          order: number;
          flexGrow: number;
        };
        expandCollapseButton?: {
          color?: string;
          backgroundColor?: string;
          borderColor?: string;
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          fontStyle?: string;
          textTransform?: string;
          textDecoration?: string;
        };
        expandCollapseDisabledButton?: {
          color: string;
          backgroundColor: string;
          borderColor?: string;
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          fontStyle?: string;
          textTransform?: string;
        };
        serviceConfirmationHeader: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
          marginTop?: string;
        };
        accordionHeaderButtonStyle?: {
          backgroundColor?: string;
          cursor?: string;
          display?: string;
          alignItems?: string;
          border?: string;
          outline?: string;
          textTransform?: string;
          paddingLeft?: string;
          marginTop?: string;
          marginLeft?: string;
          marginBottom?: string;
        };
        titleType1?: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
        };
        accordionHeader?: {
          fontFamily?: string;
          fontWeight?: number;
          letterSpacing?: number;
          lineHeight?: string;
          textAlign?: string;
          fontSize?: number;
          color?: string;
        };
        accordionRowHeader?: {
          fontFamily?: string;
          fontWeight?: number;
          letterSpacing?: number;
          lineHeight?: string;
          textAlign?: string;
          fontSize?: number;
          color?: string;
          variant?: string;
          elevation?: number;
        };
        accordionRowGridItemStyle?: {
          margin?: string;
        };
        claimSummaryDetailItemStyle?: {
          margin?: string;
        };
        adjusterNameStyle?: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
        };
        adjusterShortNameStyle?: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
        };
        primaryDescription?: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
          lineHeight?: string;
        };
        primaryLabel?: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
        };
        secondaryDescription?: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
        };
        formLabelStyle?: {
          fontFamily: string;
          fontSize: number;
          fontWeight: number;
          letterSpacing?: number;
          lineHeight?: string;
          textAlign?: string;
          color: string;
        };
        formTextStyle?: {
          fontFamily: string;
          fontSize: number;
          fontWeight: number;
          letterSpacing?: number;
          lineHeight?: string;
          textAlign?: string;
          color: string;
        };
        disclaimerFont?: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        claimSummaryDateFont?: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        monthShortNameFont?: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        errorSubcaseFont?: {
          fontFamily: string;
          fontWeight: number;
          fontSize: number;
          color: string;
        };
        providerTitleStyle?: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
        };
        phoneNumberAndWebSiteStyle?: {
          fontWeight?: number;
          fontSize?: number;
          textDecoration?: string;
          color?: string;
          fontFamily?: string;
        };
        serviceProviderCardStyle?: {
          padding?: string;
          '&:hover'?: {
            border?: string;
          };
          border?: string;
          boxShadow?: string;
          borderRadius?: string;
        };
        serviceProviderSelectedCardStyle?: {
          padding?: string;
          border?: string;
          color?: string;
          boxShadow?: string;
          borderRadius?: string;
        };
        contactCardHeaderRowStyle?: {
          fontFamily?: string;
          fontWeight?: number;
          fontSize?: number;
          color?: string;
          marginBottom?: string;
          marginTop?: string;
        };
        rentalAmountStyle?: {
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
          letterSpacing?: number;
          lineHeight?: string;
          textAlign?: string;
          color?: string;
        };
        editOnReviewStyle?: {
          width?: string;
          textAlign?: string;
          marginRight?: string;
          textDecoration?: string;
          cursor?: string;
          fontFamily?: string;
          fontSize?: number;
          fontWeight?: number;
        };
        themeType?: {
          currentThemeType?: ThemeType;
        };
        contactCardRowStyling?: {
          backgroundColor?: string;
          padding?: string;
        };
        oddRow: {
          display: string;
          alignItems: string;
          backgroundColor: string;
        };
        evenRow: {
          display: string;
          alignItems: string;
          backgroundColor: string;
        };
        hrWrapperOdd?: {
          background?: string;
          flexBasis?: string;
        };
        hrWrapperEven?: {
          background?: string;
          flexBasis?: string;
        };
        hrStyle: string;
        ctaHrStyle?: string;
        hrDashedStyle?: string;
        progressStepperComplete?: string;
        progressStepperSkipped?: string;
        progressStepperBackground?: string;
        logoWidth?: string;
        logoHeight?: string;
        serviceProviderCardHr?: string;
        serviceProviderSummaryHr?: string;
        singleServiceSummaryHrStyle?: string;
        modalHeaderHrStyle?: {
          height?: string;
          backgroundColor?: string;
          border?: string;
          width?: string;
        };
        listItemDividerHrStyle?: {
          height?: string;
          backgroundColor?: string;
          border?: string;
          width?: string;
        };
        genericModalAlertBoxStyle?: {
          distributionDirection?: string;
          contentJustifyType?: string;
          alignmentType?: string;
          spacing?: number;
          primaryActionButtonStyle?: {
            backgroundColor?: string;
            borderColor?: string;
            color?: string;
            width?: string;
            hoverBackgroundColor?: string;
            hoverTextColor?: string;
            fontFamily?: string;
            fontSize?: number;
            fontWeight?: number;
            textTransform?: string;
            buttonOrder?: number;
          };
        };
        genericModalDestructiveAlertBoxStyle?: {
          distributionDirection?: string;
          contentJustifyType?: string;
          alignmentType?: string;
          spacing?: number;
          primaryActionButtonStyle?: {
            backgroundColor?: string;
            borderColor?: string;
            color?: string;
            width?: string;
            fontFamily?: string;
            hoverBackgroundColor?: string;
            hoverTextColor?: string;
            fontSize?: number;
            fontWeight?: number;
            textTransform?: string;
            buttonOrder?: number;
          };
        };
        genericModalCancelAlertBoxStyle?: {
          distributionDirection?: string;
          contentJustifyType?: string;
          alignmentType?: string;
          spacing?: number;
          primaryActionButtonStyle?: {
            backgroundColor?: string;
            borderColor?: string;
            color?: string;
            width?: string;
            fontFamily?: string;
            hoverBackgroundColor?: string;
            hoverTextColor?: string;
            fontSize?: number;
            fontWeight?: number;
            textTransform?: string;
            buttonOrder?: number;
          };
        };
        singleServiceSummaryDashedHr?: boolean;
        showGapBetweenCtaButtons?: boolean;
        showSmallRadioButton?: boolean;
        showBorderAroundListItems?: boolean;
        showUnderlinedClaimActivityHeader?: boolean;
        showUnderlinedAccordionRowHeader?: boolean;
        listItemBorderSelectedStyle?: string;
        listItemBorderDefaultStyle?: string;
        underLineModalHeader?: boolean;
        underLineModalDescription?: boolean;
        showIconOnRisks?: boolean;
        showThickBorderForUnselectedListItem?: boolean;
        modalActionButtonDirection?: string;
        showModalPopUpOnFNOLCancel?: boolean;
        activityIndicatorHasElevation?: boolean;
        endIcon: {
          marginLeft: string;
          marginRight: string;
        };
        progressBar?: {
          backgroundColor?: string;
          fillColor?: string;
          progressTextColor?: string;
          statusBarsProgressText?: string;
          borderColor?: string;
        };
        checkBox?: {
          defaultBackground?: string;
          defaultBorder?: string;
          selectedBackground?: string;
          selectedBorder?: string;
          selectedCheckmarkColor?: string;
        };
        goToClaimButtonGridStyle?: {
          marginLeft?: string;
          paddingTop?: string;
          justifyContent?: string;
          display?: string;
        };
        borderRadius?: {
          radiusDefault?: string;
          radiusInput?: string;
          radiusModal?: string;
          radiusProgressBar?: string;
        };
      };
      icons?: {
        iconAuto?: string;
        iconProperty?: string;
        iconVinLocations?: string;
      };
    };
  }
}
